// Tries to be a more flexible implementation of fetch()
// Still work in progress

import {isPlainObject} from "./Utils";

// Parse the headers from an xhr object, to return a native Headers object
export function parseHeaders(xhr: XMLHttpRequest): Headers {
    const rawHeader = xhr.getAllResponseHeaders() || "";
    const rawHeaderLines = rawHeader.split(/\r?\n/);
    const headers = new Headers();

    for (let line of rawHeaderLines) {
        const parts = line.split(":");
        const key = (parts.shift() || "").trim();
        if (key) {
            let value = parts.join(":").trim();
            headers.append(key, value);
        }
    }
    return headers;
}

// A value a query parameter is written with, which URLSearchParams stringifies
export type URLParamValue = string | number | boolean;

// How a request's query is written: a plain object of values, or an already built URLSearchParams
export type URLSearchParamsSource = Record<string, URLParamValue | URLParamValue[]> | URLSearchParams;

// Creates a new URLSearchParams object from a plain object
// Fields that are arrays are spread
export function getURLSearchParams(data: URLSearchParamsSource, arrayKeySuffix: string = "[]"): URLSearchParams {
    if (!isPlainObject(data)) {
        return data;
    }

    const urlSearchParams = new URLSearchParams();
    for (const key of Object.keys(data)) {
        const value = data[key];
        if (Array.isArray(value)) {
            for (let instance of value) {
                urlSearchParams.append(key + arrayKeySuffix, instance);
            }
        } else {
            urlSearchParams.set(key, value);
        }
    }
    return urlSearchParams;
}

// Appends search parameters from an object to a given URL or Request, and returns the new URL
export function composeURL(url: string | Request, urlSearchParams?: URLSearchParams): string {
    let urlString: string;
    if (typeof url === 'object' && 'url' in url) {
        urlString = url.url;
    } else {
        urlString = url as string;
    }
    // TODO: also extract the preexisting arguments in the url
    if (urlSearchParams) {
        urlString += "?" + urlSearchParams;
    }
    return urlString;
}

export type DataType = "arrayBuffer" | "blob" | "formData" | "json" | "text";

// The payload stays open where the internals below use `unknown`: a postprocessor is written against one
// endpoint and reads its fields, so `unknown` costs a cast at every one - measured at 8 downstream
export type FetchPostprocessor = (payload: any, xhrPromise?: XHRPromise) => unknown;
export type FetchErrorPostprocessor = (error: any) => unknown;
export type FetchPreprocessor = (options: FetchOptions, input?: RequestInfo) => FetchOptions | void;

// Called with whatever rejected the request, which every site passes exactly one of
export type FetchErrorHandler = (error: any) => void;

export interface FetchOptions extends Omit<RequestInit, "cache"> {
    url?: string;
    // Normalized to a RequestCache below, where false means a cache-busting parameter on the URL
    cache?: RequestCache | boolean;
    dataType?: DataType;
    onUploadProgress?: (event: ProgressEvent) => void;
    onDownloadProgress?: (event: ProgressEvent) => void;
    onSuccess?: (...args: any[]) => void; // Left open: resolve forwards its own arguments
    onError?: FetchErrorHandler;
    onComplete?: () => void;
    success?: (...args: any[]) => void;
    error?: FetchErrorHandler;
    complete?: () => void;
    errorHandler?: FetchErrorHandler;
    postprocessors?: FetchPostprocessor[];
    errorPostprocessors?: FetchErrorPostprocessor[];
    preprocessors?: FetchPreprocessor[];
    urlParams?: URLSearchParamsSource;
    urlSearchParams?: URLSearchParams;
    arraySearchParamSuffix?: string;
    disableStateImport?: boolean;
    disableEventsImport?: boolean;
    
    // jQuery compatibility
    type?: string;
    // Only read when truthy, so false is how a caller says to leave the header off
    contentType?: string | false;
    // Left open: it is a plain object, which becomes the query on a GET, a JSON body under a JSON content type and a FormData otherwise, but every
    // caller writes a named request interface, and those carry no index signature to match against
    data?: any;

}

export interface URLFetchOptions extends FetchOptions {
    url: string;
}

export class XHRPromise {
    options: FetchOptions;
    request: Request;
    xhr: XMLHttpRequest;
    promise: Promise<any>;
    promiseResolve: (value?: unknown) => void;
    promiseReject: (reason?: unknown) => void;
    _chained?: boolean;

    constructor(request: RequestInfo, options: FetchOptions = {}) {
        // fetch() has normalized cache to a RequestCache by the time a request is built from these
        request = new Request(request, options as RequestInit);
        let xhr = new XMLHttpRequest();
        this.options = options;
        this.request = request;
        this.xhr = xhr;

        this.promise = new Promise((resolve, reject) => {
            this.promiseResolve = resolve;
            this.promiseReject = reject;

            xhr.onload = () => {
                let headers = this.getResponseHeaders();

                let body = xhr.response || xhr.responseText;
                let responseInit = {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    headers: headers,
                    url: xhr.responseURL || headers.get("X-Request-URL"),
                };
                let response = new Response(body, responseInit);
                // In case dataType is "arrayBuffer", "blob", "formData", "json", "text"
                // Response has methods to return these as promises
                const {dataType} = options;
                if (dataType && typeof response[dataType] === "function") {
                    const responsePromise = response[dataType]();
                    // TODO: should whitelist dataType to json, blob
                    responsePromise.then((data) => {
                        this.resolve(data);
                    }, (err) => {
                        this.reject(err);
                    });
                } else {
                    this.resolve(response);
                }
            };

            // TODO: also dispatch all arguments here on errors
            xhr.onerror = () => {
                this.reject(new TypeError("Network error"));
            };

            // TODO: need to have an options to pass setting to xhr (like timeout value)
            xhr.ontimeout = () => {
                this.reject(new TypeError("Network timeout"));
            };

            xhr.open(request.method, request.url, true);

            const {onUploadProgress, onDownloadProgress} = this.options;

            if (onUploadProgress) {
               xhr.upload.onprogress = onUploadProgress;
            }

            if (onDownloadProgress) {
                xhr.onprogress = onDownloadProgress;
            }

            if (request.credentials === "include") {
                xhr.withCredentials = true;
            }

            // TODO: come back to this
            xhr.responseType = "blob";

            let isApplicationTypeJson = false;
            request.headers.forEach((value, name) => {
                if (options.body instanceof FormData && name.toLowerCase() === "content-type") {
                    return;
                }
                // check if the request in JSON object based on headers
                if (value === "application/json" && name.toLowerCase() === "content-type") {
                    isApplicationTypeJson = true;
                }
                xhr.setRequestHeader(name, value);
            });
            // TODO: there's no need to send anything on a GET or HEAD
            if (options.body instanceof FormData) {
                this.send(options.body);
            } else if (isApplicationTypeJson) {
                // A body that is already a string was serialized by the caller
                this.send(typeof options.body === "string" ? options.body : JSON.stringify(options.body));
            } else {
                request.blob().then((blob) => {
                    // The blob can be a FormData when we're polyfilling the Request class
                    const body = ((blob instanceof FormData) || blob.size) ? blob : null;
                    this.send(body);
                });
            }
        });
    }

    getResponseHeaders() {
        return parseHeaders(this.xhr);
    }

    send(body: XMLHttpRequestBodyInit | null): void {
        this.getXHR().send(body);
    }

    getPostprocessors(): FetchPostprocessor[] {
        return this.options.postprocessors || fetch.defaultPostprocessors;
    }

    getErrorPostprocessors(): FetchErrorPostprocessor[] {
        return this.options.errorPostprocessors || fetch.defaultErrorPostprocessors;
    }

    resolve(payload: unknown): void {
        for (const postprocessor of this.getPostprocessors()) {
            try {
                payload = postprocessor(payload, this) || payload;
            } catch (exception) {
                this.reject(exception);
                return;
            }
        }

        if (this.options.onSuccess) {
            this.options.onSuccess(...arguments);
        } else {
            this.promiseResolve(...arguments);
        }
        if (this.options.onComplete) {
            this.options.onComplete();
        }
    }

    reject(error: unknown): void {
        for (const postprocessor of this.getErrorPostprocessors()) {
            error = postprocessor(error) || error;
        }

        if (this.options.onError) {
            this.options.onError(error);
        } else {
            if (this._chained) {
                this.promiseReject(error);
            } else {
                if (this.options.errorHandler) {
                    this.options.errorHandler(error);
                } else {
                    console.error("Unhandled fetch error", error);
                }
            }
        }
        if (this.options.onComplete) {
            this.options.onComplete();
        }
    }

    // TODO: next 2 functions should throw an exception if you have onSuccess/onError
    then(onResolve?: (value: any) => any, onReject?: (reason: any) => any): Promise<any> {
        this._chained = true;
        onReject = onReject || this.options.errorHandler;
        return this.getPromise().then(onResolve, onReject);
    }

    catch(onReject?: (reason: any) => any): Promise<any> {
        this._chained = true;
        return this.getPromise().catch(onReject);
    }

    getXHR(): XMLHttpRequest {
        return this.xhr;
    }

    getPromise(): Promise<any> {
        return this.promise;
    }

    getRequest(): Request {
        return this.request;
    }

    abort(): void {
        this.getXHR().abort();
    }

    addXHRListener(name: string, callback: EventListener, ...args: [(boolean | AddEventListenerOptions)?]): void {
        this.getXHR().addEventListener(name, callback, ...args);
    }

    addProgressListener(callback: (event: ProgressEvent) => void, ...args: [(boolean | AddEventListenerOptions)?]): void {
        this.addXHRListener("progress", callback, ...args);
    }
}

// TODO: this offers only partial compatibility with $.ajax, remove
export function jQueryCompatibilityPreprocessor(options: FetchOptions): FetchOptions {
    if (options.type) {
        options.method = options.type.toUpperCase();
    }

    const headers = options.headers as Headers;
    headers.set("X-Requested-With", "XMLHttpRequest");

    if (options.contentType) {
        headers.set("Content-Type", options.contentType);
    }

    if (isPlainObject(options.data)) {
        const method = (options.method || "GET").toUpperCase();
        if (method === "GET" || method === "HEAD") {
            options.urlParams = options.urlParams || options.data;
            if (options.cache === false) {
                options.urlParams = getURLSearchParams(options.urlParams, options.arraySearchParamSuffix);
                options.urlParams.set("_", String(Date.now()));
            }
        } else if (options.contentType === "application/json") {
            options.body = JSON.stringify(options.data);
        } else {
            let formData = new FormData();
            for (const key of Object.keys(options.data)) {
                const value = options.data[key];
                if (Array.isArray(value)) {
                    for (const arrayValue of value) {
                        formData.append(key + "[]", arrayValue);
                    }
                } else {
                    formData.append(key, value);
                }

            }
            options.body = formData;
        }
    } else {
        options.body = options.body || options.data;
    }
    return options;
}

// Can either be called with
// - 1 argument: (Request)
// - 2 arguments: (url/Request, options)
export function fetch(input: RequestInfo | URLFetchOptions, ...args: FetchOptions[]): XHRPromise {
    // In case we're being passed in a single plain object (not Request), assume it has a url field
    if (isPlainObject(input)) {
        return fetch(input.url, input, ...args);
    }

    let options = Object.assign({}, ...args);

    // Ensure that there's a .headers field for preprocessors
    options.headers = new Headers(options.headers || {});

    const preprocessors = options.preprocessors || fetch.defaultPreprocessors || [];

    for (const preprocessor of preprocessors) {
        options = preprocessor(options, input) || options;
    }

    options.onSuccess = options.onSuccess || options.success;
    options.onError = options.onError || options.error;
    options.onComplete = options.onComplete || options.complete;

    if (typeof options.cache === "boolean") {
        options.cache = options.cache ? "force-cache" : "reload";
        // TODO: cache still isn't fully done
    }

    options.method = options.method || "GET";
    // If there are any url search parameters, update the url from the urlParams or urlSearchParams fields
    // These fields can be plain objects (jQuery style) or can be URLSearchParams objects
    const urlParams = options.urlParams || options.urlSearchParams;
    if (urlParams) {
        // Change the URL of the request to add a query
        const urlSearchParams = getURLSearchParams(urlParams, options.arraySearchParamSuffix);
        // Cast because the union admits a string, and instanceof refuses a left side that might be a primitive
        if ((input as unknown) instanceof Request) {
            input = new Request(composeURL((input as unknown as Request).url, urlSearchParams), input as RequestInit);
        } else {
            input = new Request(composeURL(input, urlSearchParams), {});
        }
    }

    // The branch above returned for a plain object, so what is left is a RequestInfo - which no guard states,
    // since a Request is an object too
    return new XHRPromise(input as RequestInfo, options);
}

fetch.defaultPreprocessors = [] as FetchPreprocessor[];
fetch.defaultPostprocessors = [] as FetchPostprocessor[];
fetch.defaultErrorPostprocessors = [] as FetchErrorPostprocessor[];

fetch.polyfill = true;


// TODO @cleanup @Mihai normalize how api clients are implemented, they should have a standard interface to be usable inside Stem methods
// PromiseLike, not Promise: the default loader answers with an XHRPromise, which is thenable and not one
export type LoaderFunction = (url: string, params?: URLSearchParamsSource) => PromiseLike<any>;

let CurrentLoaderFunc: LoaderFunction = (url: string, params?: URLSearchParamsSource) => {
    return fetch(url, {urlParams: params});
}

export function SetLoaderFunc(func: LoaderFunction): void {
    CurrentLoaderFunc = func;
}

export function LoadEndpoint(url: string, params?: URLSearchParamsSource) {
    return CurrentLoaderFunc(url, params);
}